import { useState, type ReactNode } from 'react';
import { TabelaCartoes } from '@/components/common/TabelaCartoes';
import { ListaMobile, ItemLista } from '@/components/common/ListaMobile';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { TrendingUp, FileDown, FileSpreadsheet } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/utils/formatters';
import { useChartColors } from '@/hooks/useChartColors';
import { useFinance } from '@/hooks/useFinance';
import { PlatformIcon } from '@/components/common/PlatformIcon';
import {
  abrirRelatorioPdf,
  baixarRelatorioCsv,
  ORIGEM_LABEL,
  planoComCiclo,
  sufixoCiclo,
} from './relatorio';
import type {
  FinanceCobrancaRecorrente,
  FinancePeriod,
  FinanceRecebido,
  FinanceRetencao,
  FinanceVencimento,
} from '@/types/finance.types';

const MESES_FULL = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const PERIOD_TABS: { value: FinancePeriod; label: string }[] = [
  { value: 'mes', label: 'Mês' },
  { value: 'trimestre', label: 'Trimestre' },
  { value: 'ano', label: 'Ano' },
];

const reais = (centavos: number) => centavos / 100;

const PERIOD_TEXTO: Record<FinancePeriod, string> = {
  mes: 'neste mês',
  trimestre: 'nos últimos 3 meses',
  ano: 'nos últimos 12 meses',
};

// No celular as listas de pagamentos e vencimentos mostram só as primeiras
// linhas; o rodapé "Ver todos" expande o resto.
const LIMITE_MOBILE = 5;

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

// Data curta (14/10) para a linha de apoio da lista mobile.
const dataCurta = (iso: string) =>
  new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

export function Finances() {
  const [period, setPeriod] = useState<FinancePeriod>('mes');
  const { data, isLoading } = useFinance(period);
  const chart = useChartColors();

  const now = new Date();
  const mesAno = `${MESES_FULL[now.getMonth()]} ${now.getFullYear()}`;
  const prevMesNome = MESES_FULL[(now.getMonth() + 11) % 12].toLowerCase();

  // Cor por categoria (donut) / por plano (tabela).
  const catColor = (label: string): string => {
    if (label === 'Cortesia') return chart.tertiary;
    if (label.includes('Profissional')) return chart.info;
    if (label.includes('Básico')) return chart.primary;
    if (label.includes('Enterprise')) return chart.warning;
    return chart.axis; // Trial / sem plano — cinza neutro
  };
  const planColor = (codigo: string): string => {
    if (codigo === 'enterprise') return chart.tertiary;
    if (codigo === 'profissional') return chart.info;
    if (codigo === 'basico') return chart.primary;
    return chart.warning; // trial
  };

  const tooltipStyles = {
    contentStyle: {
      backgroundColor: chart.tooltipBg,
      border: `1px solid ${chart.tooltipBorder}`,
      borderRadius: 12,
      fontSize: 12,
      color: chart.textBody,
    },
    labelStyle: { color: chart.textTitle, fontWeight: 600 },
  } as const;

  const evolucaoData =
    data?.evolucao.map((e) => ({
      mes: e.mes,
      mrr: reais(e.mrrCentavos),
      recebido: reais(e.recebidoCentavos ?? 0),
      novas: e.novasAssinaturas,
    })) ?? [];

  return (
    <Layout breadcrumbs={[{ label: 'Admin', path: '/admin' }, { label: 'Finanças' }]}>
      <div className="flex flex-col gap-4 p-3 sm:p-4 lg:gap-6 lg:p-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="max-md:hidden text-2xl font-bold tracking-tight text-text-title sm:text-3xl">
              Finanças
            </h1>
            <p className="text-sm text-text-secondary">{mesAno} · valores em reais</p>
          </div>
          <div className="flex items-center gap-2 max-md:w-full">
            <div
              className="flex rounded-lg border border-border bg-card p-1 max-md:flex-1"
              role="group"
              aria-label="Período"
            >
              {PERIOD_TABS.map((t) => {
                const active = period === t.value;
                return (
                  <button
                    key={t.value}
                    onClick={() => setPeriod(t.value)}
                    aria-pressed={active}
                    className={cn(
                      'rounded-md px-3 py-1.5 text-sm font-medium max-md:flex-1 max-md:px-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                      active
                        ? 'bg-primary text-bg-main shadow-card'
                        : 'text-text-secondary hover:text-text-title'
                    )}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
            <Button
              variant="secondary"
              onClick={() => data && baixarRelatorioCsv(data)}
              disabled={!data}
            >
              <FileSpreadsheet size={16} />
              CSV
            </Button>
            <Button onClick={() => data && abrirRelatorioPdf(data)} disabled={!data}>
              <FileDown size={16} />
              <span className="max-md:hidden">Relatório PDF</span>
              <span className="md:hidden">PDF</span>
            </Button>
          </div>
        </div>

        {isLoading || !data ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-28 animate-pulse rounded-xl border border-border bg-card" />
            ))}
          </div>
        ) : (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-5">
              <div className="col-span-2 rounded-xl border border-primary/50 bg-primary/5 p-4 shadow-card sm:col-span-1 sm:p-5">
                <p className="text-sm font-medium text-primary">Receita recorrente (MRR)</p>
                <p className="mt-1 text-3xl font-bold tracking-tight text-text-title">
                  {formatCurrency(reais(data.mrr.totalCentavos))}
                </p>
                <p
                  className={cn(
                    'mt-1.5 flex items-center gap-1 text-sm font-medium',
                    data.mrr.deltaCentavos > 0 ? 'text-success' : 'text-text-secondary'
                  )}
                >
                  {data.mrr.deltaCentavos > 0 && <TrendingUp size={14} />}
                  {data.mrr.deltaCentavos > 0
                    ? `+${formatCurrency(reais(data.mrr.deltaCentavos))} vs ${prevMesNome}`
                    : `sem variação vs ${prevMesNome}`}
                </p>
              </div>

              {data.recebido && (
                <KpiCard
                  wide
                  label={`Recebido ${PERIOD_TEXTO[period]}`}
                  value={formatCurrency(reais(data.recebido.totalCentavos))}
                  subtitle={
                    data.recebido.pagamentos === 0
                      ? 'nenhum pagamento registrado'
                      : data.recebido.liquidoCentavos != null
                        ? `${formatCurrency(reais(data.recebido.liquidoCentavos))} líquido estimado · ${data.recebido.pagamentos} ${data.recebido.pagamentos === 1 ? 'pagamento' : 'pagamentos'}`
                        : `${data.recebido.pagamentos} ${data.recebido.pagamentos === 1 ? 'pagamento' : 'pagamentos'} de ${data.recebido.clientes} ${data.recebido.clientes === 1 ? 'cliente' : 'clientes'}`
                  }
                />
              )}
              <KpiCard
                label="Pagantes hoje"
                value={String(data.pagantes.count)}
                valueClass="text-success"
                subtitle={`de ${data.pagantes.totalUsers} usuários · ${data.pagantes.pct}%`}
              />
              <KpiCard
                label="Não pagantes"
                value={String(data.naoPagantes.count)}
                valueClass="text-warning"
                subtitle={`${data.naoPagantes.cortesias} cortesias · ${data.naoPagantes.trials} trial · ${data.naoPagantes.semPlano} sem plano`}
              />
              <KpiCard
                wide
                label="Ticket médio"
                value={formatCurrency(reais(data.ticketMedioCentavos))}
                subtitle="por pagante / mês"
              />
            </div>

            {data.retencao && (
              <RetencaoFaixa
                retencao={data.retencao}
                arrCentavos={data.arrCentavos ?? data.mrr.totalCentavos * 12}
                periodoTexto={PERIOD_TEXTO[period]}
              />
            )}

            {data.cobrancaRecorrente && (
              <CobrancaRecorrenteFaixa
                dados={data.cobrancaRecorrente}
                periodoTexto={PERIOD_TEXTO[period]}
              />
            )}

            {/* Gráficos */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-6">
              {/* Evolução */}
              <div className="rounded-xl border border-border bg-card p-5 shadow-card lg:col-span-2">
                <h3 className="mb-4 text-lg font-semibold text-text-title">Evolução da receita</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={evolucaoData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
                    <XAxis
                      dataKey="mes"
                      stroke={chart.axis}
                      tick={{ fill: chart.axis }}
                      fontSize={12}
                      tickLine={false}
                      axisLine={{ stroke: chart.grid }}
                    />
                    <YAxis
                      stroke={chart.axis}
                      tick={{ fill: chart.axis }}
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                      width={40}
                    />
                    <Tooltip
                      {...tooltipStyles}
                      formatter={(value: number, name: string) =>
                        name === 'Novas assinaturas' ? value : formatCurrency(value)
                      }
                    />
                    <Legend iconType="plainline" wrapperStyle={{ fontSize: 12 }} />
                    <Line
                      type="monotone"
                      dataKey="mrr"
                      name="MRR"
                      stroke={chart.primary}
                      strokeWidth={2.5}
                      dot={false}
                      activeDot={{ r: 5, fill: chart.primary }}
                    />
                    {data.recebido && (
                      <Line
                        type="monotone"
                        dataKey="recebido"
                        name="Recebido no mês"
                        stroke={chart.info}
                        strokeWidth={2.5}
                        dot={{ r: 3, fill: chart.info }}
                        activeDot={{ r: 5, fill: chart.info }}
                      />
                    )}
                    <Line
                      type="monotone"
                      dataKey="novas"
                      name="Novas assinaturas"
                      stroke={chart.tertiary}
                      strokeWidth={2.5}
                      dot={false}
                      activeDot={{ r: 5, fill: chart.tertiary }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Donut */}
              <div className="rounded-xl border border-border bg-card p-5 shadow-card">
                <h3 className="mb-4 text-lg font-semibold text-text-title">Usuários por plano</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={data.usuariosPorPlano}
                      dataKey="count"
                      nameKey="label"
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={2}
                      stroke="none"
                    >
                      {data.usuariosPorPlano.map((s) => (
                        <Cell key={s.label} fill={catColor(s.label)} />
                      ))}
                    </Pie>
                    <Tooltip {...tooltipStyles} />
                  </PieChart>
                </ResponsiveContainer>
                <ul className="mt-3 space-y-1.5">
                  {data.usuariosPorPlano.map((s) => (
                    <li key={s.label} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-text-secondary">
                        <span
                          className="size-2.5 rounded-full"
                          style={{ backgroundColor: catColor(s.label) }}
                        />
                        {s.label}
                      </span>
                      <span className="font-semibold tabular-nums text-text-title">{s.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Tabela de planos — no celular vira lista */}
            <ListaMobile className="lg:hidden" titulo="Planos">
              {data.planos.map((p) => (
                <ItemLista
                  key={p.codigo}
                  inicio={
                    <span
                      className="block size-2.5 rounded-full"
                      style={{ backgroundColor: planColor(p.codigo) }}
                      aria-hidden="true"
                    />
                  }
                  titulo={p.nome}
                  apoio={[
                    p.precoLabel,
                    p.assinantes == null
                      ? '— assinantes'
                      : plural(p.assinantes, 'assinante', 'assinantes'),
                    p.pagantes == null ? '— pagantes' : plural(p.pagantes, 'pagante', 'pagantes'),
                  ].join(' · ')}
                  fim={
                    <>
                      <span className="text-[15px] font-bold tabular-nums text-text-title">
                        {formatCurrency(reais(p.receitaMesCentavos))}
                      </span>
                      <span className="tabular-nums">{p.pctMrr}% do MRR</span>
                    </>
                  }
                />
              ))}
            </ListaMobile>

            <div className="hidden w-full overflow-hidden rounded-xl border border-border bg-card shadow-card lg:block">
              <div className="overflow-x-auto">
                <TabelaCartoes className="w-full text-sm">
                  <thead className="border-b border-border bg-muted/40 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    <tr>
                      <th className="px-5 py-3">Plano</th>
                      <th className="px-5 py-3">Preço</th>
                      <th className="px-5 py-3 text-right">Assinantes</th>
                      <th className="px-5 py-3 text-right">Pagantes</th>
                      <th className="px-5 py-3 text-right">Receita / mês</th>
                      <th className="px-5 py-3">% do MRR</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.planos.map((p) => (
                      <tr key={p.codigo} className="transition-colors hover:bg-muted/40">
                        <td className="px-5 py-3">
                          <span className="flex items-center gap-2 font-medium text-text-title">
                            <span
                              className="size-2.5 rounded-full"
                              style={{ backgroundColor: planColor(p.codigo) }}
                            />
                            {p.nome}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-text-secondary">{p.precoLabel}</td>
                        <td className="px-5 py-3 text-right tabular-nums text-text-body">
                          {p.assinantes ?? '—'}
                        </td>
                        <td className="px-5 py-3 text-right tabular-nums text-text-body">
                          {p.pagantes ?? '—'}
                        </td>
                        <td className="px-5 py-3 text-right font-semibold tabular-nums text-text-title">
                          {formatCurrency(reais(p.receitaMesCentavos))}
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <div className="h-1.5 w-full max-w-32 overflow-hidden rounded-full bg-muted">
                              <div
                                className="h-full rounded-full"
                                style={{
                                  width: `${p.pctMrr}%`,
                                  backgroundColor: planColor(p.codigo),
                                }}
                              />
                            </div>
                            <span className="w-9 shrink-0 text-right text-xs tabular-nums text-text-secondary">
                              {p.pctMrr}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </TabelaCartoes>
              </div>
            </div>

            {data.recebido && (
              <PagamentosTabela recebido={data.recebido} periodoTexto={PERIOD_TEXTO[period]} />
            )}

            {data.vencendo && <VencimentosTabela vencendo={data.vencendo} />}
          </>
        )}
      </div>
    </Layout>
  );
}

/** Histórico do que entrou de dinheiro, inclusive de assinatura que já venceu. */
function PagamentosTabela({
  recebido,
  periodoTexto,
}: {
  recebido: FinanceRecebido;
  periodoTexto: string;
}) {
  const [verTodos, setVerTodos] = useState(false);
  const descricao =
    'Compras e renovações nas lojas, cobranças pagas pela AbacatePay (inclusive avulsas), baixas manuais e assinaturas pagas criadas no painel. Anual entra com o valor inteiro. Teste grátis, cortesia e reembolso não entram.';
  const visiveis = verTodos ? recebido.itens : recebido.itens.slice(0, LIMITE_MOBILE);

  return (
    <>
      {/* Celular e tablet: lista no padrão de app */}
      <div className="flex flex-col gap-2 lg:hidden">
        <ListaMobile
          titulo={`Pagamentos ${periodoTexto}`}
          resumo={
            <span className="text-[13px] font-semibold tabular-nums text-success">
              {formatCurrency(reais(recebido.totalCentavos))}
            </span>
          }
          rodape={
            recebido.itens.length > LIMITE_MOBILE && (
              <RodapeVerTodos
                aberto={verTodos}
                onToggle={() => setVerTodos((v) => !v)}
                texto={`Ver todos os ${recebido.itens.length} pagamentos`}
              />
            )
          }
        >
          {recebido.itens.length === 0 ? (
            <VazioMobile>Nenhum pagamento registrado {periodoTexto}.</VazioMobile>
          ) : (
            visiveis.map((pg) => (
              <ItemLista
                key={`${pg.userId}-${pg.data}`}
                titulo={pg.cliente ?? `Usuário ${pg.userId}`}
                apoio={[
                  planoComCiclo(pg.plano, pg.ciclo),
                  ORIGEM_LABEL[pg.origem],
                  dataCurta(pg.data),
                ].join(' · ')}
                fim={
                  <>
                    <span className="text-[15px] font-bold tabular-nums text-text-title">
                      {formatCurrency(reais(pg.valorCentavos))}
                    </span>
                    <span className="tabular-nums">
                      líq. {formatCurrency(reais(pg.liquidoCentavos ?? pg.valorCentavos))}
                    </span>
                  </>
                }
              />
            ))
          )}
        </ListaMobile>
        <p className="px-1 text-xs text-text-hint">{descricao}</p>
      </div>

      <PagamentosTabelaDesktop
        recebido={recebido}
        periodoTexto={periodoTexto}
        descricao={descricao}
      />
    </>
  );
}

/** Rodapé da lista mobile que expande (ou recolhe) as linhas além do limite. */
function RodapeVerTodos({
  aberto,
  onToggle,
  texto,
}: {
  aberto: boolean;
  onToggle: () => void;
  texto: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={aberto}
      className="w-full text-left text-[13px] font-semibold text-primary"
    >
      {aberto ? 'Mostrar menos' : texto}
    </button>
  );
}

/** Linha de vazio dentro da lista mobile (fica dentro do <ul>). */
function VazioMobile({ children }: { children: ReactNode }) {
  return <li className="px-4 py-8 text-center text-sm text-text-secondary">{children}</li>;
}

function PagamentosTabelaDesktop({
  recebido,
  periodoTexto,
  descricao,
}: {
  recebido: FinanceRecebido;
  periodoTexto: string;
  descricao: string;
}) {
  return (
    <div className="hidden w-full overflow-hidden rounded-xl border border-border bg-card shadow-card lg:block">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border px-5 py-4">
        <h3 className="text-lg font-semibold text-text-title">Pagamentos {periodoTexto}</h3>
        <p className="text-sm text-text-secondary">{descricao}</p>
      </div>
      {recebido.itens.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-text-secondary">
          Nenhum pagamento registrado {periodoTexto}.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <TabelaCartoes className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
              <tr>
                <th className="px-5 py-3">Data</th>
                <th className="px-5 py-3">Cliente</th>
                <th className="px-5 py-3">Origem</th>
                <th className="px-5 py-3">Plano</th>
                <th className="px-5 py-3 text-right">Valor</th>
                <th className="px-5 py-3 text-right">Líquido</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {recebido.itens.map((pg) => (
                <tr key={`${pg.userId}-${pg.data}`} className="transition-colors hover:bg-muted/40">
                  <td className="px-5 py-3 tabular-nums text-text-body">
                    {new Date(pg.data).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-5 py-3 font-medium text-text-title">
                    {pg.cliente ?? `Usuário ${pg.userId}`}
                  </td>
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center gap-1.5 text-text-body">
                      {(pg.origem === 'apple' || pg.origem === 'google') && (
                        <PlatformIcon
                          platform={pg.origem === 'apple' ? 'ios' : 'android'}
                          size={13}
                          colored
                        />
                      )}
                      {ORIGEM_LABEL[pg.origem]}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-text-secondary">
                    {planoComCiclo(pg.plano, pg.ciclo)}
                  </td>
                  <td className="px-5 py-3 text-right font-semibold tabular-nums text-text-title">
                    {formatCurrency(reais(pg.valorCentavos))}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums text-text-secondary">
                    {formatCurrency(reais(pg.liquidoCentavos ?? pg.valorCentavos))}
                  </td>
                </tr>
              ))}
            </tbody>
          </TabelaCartoes>
        </div>
      )}
    </div>
  );
}

/** Faturas da cobrança recorrente: o que está para receber, em atraso e a inadimplência. */
function CobrancaRecorrenteFaixa({
  dados,
  periodoTexto,
}: {
  dados: FinanceCobrancaRecorrente;
  periodoTexto: string;
}) {
  const plural = (n: number) => `${n} fatura${n === 1 ? '' : 's'}`;
  const itens: { label: string; value: string; hint: string; tone?: string }[] = [
    {
      label: 'A receber',
      value: formatCurrency(reais(dados.aReceber.totalCentavos)),
      hint: `${plural(dados.aReceber.faturas)} em aberto`,
    },
    {
      label: 'Em atraso',
      value: formatCurrency(reais(dados.emAtraso.totalCentavos)),
      hint: `${plural(dados.emAtraso.faturas)} vencida${dados.emAtraso.faturas === 1 ? '' : 's'}`,
      tone: dados.emAtraso.faturas > 0 ? 'text-error' : undefined,
    },
    {
      label: 'Inadimplência',
      value: dados.inadimplenciaPct == null ? '—' : `${dados.inadimplenciaPct}%`,
      hint: `das faturas que venceram ${periodoTexto}`,
      tone:
        dados.inadimplenciaPct != null && dados.inadimplenciaPct > 0 ? 'text-warning' : undefined,
    },
  ];
  return (
    <div className="grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-border bg-border sm:grid-cols-3">
      {itens.map((i) => (
        <div key={i.label} className="bg-card px-5 py-4">
          <p className="text-sm text-text-secondary">{i.label}</p>
          <p className={cn('mt-1 text-2xl font-bold tracking-tight text-text-title', i.tone)}>
            {i.value}
          </p>
          <p className="mt-0.5 text-xs text-text-secondary">{i.hint}</p>
        </div>
      ))}
    </div>
  );
}

/** Retenção no período: cancelamentos, testes grátis e conversão, mais o ARR. */
function RetencaoFaixa({
  retencao,
  arrCentavos,
  periodoTexto,
}: {
  retencao: FinanceRetencao;
  arrCentavos: number;
  periodoTexto: string;
}) {
  const itens: { label: string; value: string; hint: string; tone?: string }[] = [
    {
      label: 'Cancelamentos',
      value: String(retencao.cancelamentos),
      hint: `quem já pagou e venceu ${periodoTexto}`,
      tone: retencao.cancelamentos > 0 ? 'text-error' : undefined,
    },
    {
      label: 'Testes grátis nas lojas',
      value: String(retencao.testesIniciados),
      hint: `iniciados ${periodoTexto}`,
    },
    {
      label: 'Conversão de teste',
      value: retencao.conversaoPct == null ? '—' : `${retencao.conversaoPct}%`,
      hint: `${retencao.testesConvertidos} de ${retencao.testesIniciados} viraram pagamento`,
      tone:
        retencao.conversaoPct == null
          ? undefined
          : retencao.conversaoPct > 0
            ? 'text-success'
            : 'text-warning',
    },
    { label: 'ARR', value: formatCurrency(reais(arrCentavos)), hint: 'MRR anualizado' },
  ];
  return (
    <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border bg-border lg:grid-cols-4">
      {itens.map((i) => (
        <div key={i.label} className="bg-card px-5 py-4">
          <p className="text-sm text-text-secondary">{i.label}</p>
          <p className={cn('mt-1 text-2xl font-bold tracking-tight text-text-title', i.tone)}>
            {i.value}
          </p>
          <p className="mt-0.5 text-xs text-text-secondary">{i.hint}</p>
        </div>
      ))}
    </div>
  );
}

/** Assinaturas pagas que vencem em até 30 dias: quem acompanhar pra não perder. */
function VencimentosTabela({ vencendo }: { vencendo: FinanceVencimento[] }) {
  const [verTodos, setVerTodos] = useState(false);
  const descricao =
    'Assinaturas pagas ativas e o valor da próxima cobrança. Nas lojas, a renovação é automática se o cliente não cancelar; pelo painel e pela AbacatePay, é manual.';
  const visiveis = verTodos ? vencendo : vencendo.slice(0, LIMITE_MOBILE);
  const totalCentavos = vencendo.reduce((soma, v) => soma + v.valorCentavos, 0);

  return (
    <>
      {/* Celular e tablet: lista no padrão de app */}
      <div className="flex flex-col gap-2 lg:hidden">
        <ListaMobile
          titulo="Vencem nos próximos 30 dias"
          resumo={
            vencendo.length > 0 && (
              <span className="text-[13px] font-semibold tabular-nums text-text-secondary">
                {formatCurrency(reais(totalCentavos))}
              </span>
            )
          }
          rodape={
            vencendo.length > LIMITE_MOBILE && (
              <RodapeVerTodos
                aberto={verTodos}
                onToggle={() => setVerTodos((v) => !v)}
                texto={`Ver todos os ${vencendo.length} vencimentos`}
              />
            )
          }
        >
          {vencendo.length === 0 ? (
            <VazioMobile>Nenhuma assinatura paga vence nos próximos 30 dias.</VazioMobile>
          ) : (
            visiveis.map((v) => (
              <ItemLista
                key={v.userId}
                titulo={v.cliente ?? `Usuário ${v.userId}`}
                apoio={`${planoComCiclo(v.plano, v.ciclo)} · ${ORIGEM_LABEL[v.origem]}`}
                fim={
                  <>
                    <span className="text-[15px] font-bold tabular-nums text-text-title">
                      {formatCurrency(reais(v.valorCentavos))}
                      {sufixoCiclo(v.ciclo)}
                    </span>
                    <span className="tabular-nums">vence {dataCurta(v.venceEm)}</span>
                  </>
                }
              />
            ))
          )}
        </ListaMobile>
        <p className="px-1 text-xs text-text-hint">{descricao}</p>
      </div>

      <VencimentosTabelaDesktop vencendo={vencendo} descricao={descricao} />
    </>
  );
}

function VencimentosTabelaDesktop({
  vencendo,
  descricao,
}: {
  vencendo: FinanceVencimento[];
  descricao: string;
}) {
  return (
    <div className="hidden w-full overflow-hidden rounded-xl border border-border bg-card shadow-card lg:block">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border px-5 py-4">
        <h3 className="text-lg font-semibold text-text-title">Vencem nos próximos 30 dias</h3>
        <p className="text-sm text-text-secondary">{descricao}</p>
      </div>
      {vencendo.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-text-secondary">
          Nenhuma assinatura paga vence nos próximos 30 dias.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <TabelaCartoes className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
              <tr>
                <th className="px-5 py-3">Vence em</th>
                <th className="px-5 py-3">Cliente</th>
                <th className="px-5 py-3">Origem</th>
                <th className="px-5 py-3">Plano</th>
                <th className="px-5 py-3 text-right">Próxima cobrança</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {vencendo.map((v) => (
                <tr key={v.userId} className="transition-colors hover:bg-muted/40">
                  <td className="px-5 py-3 tabular-nums text-text-body">
                    {new Date(v.venceEm).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-5 py-3 font-medium text-text-title">
                    {v.cliente ?? `Usuário ${v.userId}`}
                  </td>
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center gap-1.5 text-text-body">
                      {(v.origem === 'apple' || v.origem === 'google') && (
                        <PlatformIcon
                          platform={v.origem === 'apple' ? 'ios' : 'android'}
                          size={13}
                          colored
                        />
                      )}
                      {ORIGEM_LABEL[v.origem]}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-text-secondary">
                    {planoComCiclo(v.plano, v.ciclo)}
                  </td>
                  <td className="px-5 py-3 text-right font-semibold tabular-nums text-text-title">
                    {formatCurrency(reais(v.valorCentavos))}
                    {sufixoCiclo(v.ciclo)}
                  </td>
                </tr>
              ))}
            </tbody>
          </TabelaCartoes>
        </div>
      )}
    </div>
  );
}

function KpiCard({
  label,
  value,
  subtitle,
  valueClass,
  wide,
}: {
  label: string;
  value: string;
  subtitle: string;
  valueClass?: string;
  // No celular ocupa a linha inteira (as outras ficam duas por linha).
  wide?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-xl border border-border bg-card p-4 shadow-card sm:col-span-1 sm:p-5',
        wide && 'col-span-2'
      )}
    >
      <p className="text-sm font-medium text-text-secondary">{label}</p>
      <p
        className={cn(
          'mt-1 text-2xl font-bold tracking-tight text-text-title sm:text-3xl',
          valueClass
        )}
      >
        {value}
      </p>
      <p className="mt-1.5 text-sm text-text-secondary">{subtitle}</p>
    </div>
  );
}
